import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { IInventoryNode } from "./inventoryTree";
import { fillRangeSerials, parsePastedSerials, sectionProgress } from "./serialAccelerators";

interface IInventorySerialSectionProps {
  node: IInventoryNode;
  path: number[];
  onChange: (path: number[], patch: Partial<IInventoryNode>) => void;
  serverErrorBySerial: Record<string, string>;
  // Exposes this section's own serial input to the parent section, so the
  // parent can include it in its own scan-to-fill auto-advance scope.
  // A plain callback prop instead of React's ref/forwardRef mechanism —
  // forwardRef's typing breaks on this component's recursive self-reference.
  inputRef?: (el: HTMLInputElement | null) => void;
}

// Left-border accent per nesting depth so a carton section and the pack
// section inside it read as visually distinct, cycling if deeper than 4 levels.
const DEPTH_ACCENTS = ["border-l-blue-400", "border-l-purple-400", "border-l-amber-400", "border-l-teal-400"];

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.415 0l-3.5-3.5a1 1 0 111.415-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414-.006z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ScanIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m-2 12h2a2 2 0 002-2v-2M4 12h16" />
    </svg>
  );
}

function RangeFillIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h13m0 0l-4-4m4 4l-4 4" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={className}>
      <rect x="7" y="5" width="10" height="15" rx="1.5" />
      <path strokeLinecap="round" d="M9 5V4a1 1 0 011-1h4a1 1 0 011 1v1" />
    </svg>
  );
}

// One packaging-level node of the inventory serial tree.
//
// The root (level 1) renders as a plain "{type} serial (N of 1)" label +
// input — no card, no collapse, no accelerator toolbar — matching the
// ticket's reference mockup where the top-level serial sits above the child
// sections rather than visually containing them. Every other level renders
// as a collapsible card with its own serial input, a Scan / Range fill /
// Paste toolbar, and either a compact grid of base-unit inputs or one
// nested InventorySerialSection per child. Scan-to-fill (Enter -> auto-
// advance) and paste-distribute are scoped to this section's own slot +
// its DIRECT children's slots only.
export function InventorySerialSection({ node, path, onChange, serverErrorBySerial, inputRef }: IInventorySerialSectionProps) {
  const ownInputRef = useRef<HTMLInputElement | null>(null);
  const childRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [rangeStart, setRangeStart] = useState("");
  const [showRangeFill, setShowRangeFill] = useState(false);
  const [collapsed, setCollapsed] = useState(node.level > 1);
  const notify = useNotification();

  const isRoot = node.level === 1;
  const isLeaf = !node.children;
  const childrenAreLeaves = !isLeaf && node.children!.every((c) => !c.children);
  const progress = sectionProgress(node);
  const isComplete = progress.filled === progress.total;
  const ownFilled = node.serial.trim().length > 0;
  const ownError = serverErrorBySerial[node.serial] || node.error;
  const depthAccent = DEPTH_ACCENTS[(node.level - 1) % DEPTH_ACCENTS.length];

  const sectionHasError = !!ownError || (node.children || []).some((c) => !!serverErrorBySerial[c.serial]);
  useEffect(() => {
    if (sectionHasError && collapsed) setCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionHasError]);

  useEffect(() => {
    if (ownError) ownInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownError]);

  function sectionSlots(): { serial: string; childIndex: number | null }[] {
    return [
      { serial: node.serial, childIndex: null },
      ...(node.children || []).map((c, i) => ({ serial: c.serial, childIndex: i })),
    ];
  }

  // fromIndex -1 focuses this section's own field first if empty, matching
  // the Scan/Paste toolbar buttons' "ready the next empty slot" behavior.
  function focusNextEmpty(fromIndex: number) {
    const refs = [ownInputRef.current, ...childRefs.current];
    for (let i = fromIndex + 1; i < refs.length; i++) {
      if (refs[i] && !refs[i]!.value.trim()) {
        refs[i]!.focus();
        return;
      }
    }
  }

  function handleKeyDown(index: number) {
    return (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        focusNextEmpty(index);
      }
    };
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const slots = sectionSlots();
    const emptySlots = slots.filter((s) => !s.serial.trim());
    if (emptySlots.length === 0) return;

    const { values, overcount, multiColumn } = parsePastedSerials(
      e.clipboardData.getData("text"),
      emptySlots.length,
    );

    if (multiColumn) {
      notify({ type: "ERROR", message: "Pasted data has multiple columns — copy a single column of serials." });
      return;
    }

    emptySlots.slice(0, values.length).forEach((slot, i) => {
      if (slot.childIndex === null) {
        onChange(path, { serial: values[i], touched: true });
      } else {
        onChange([...path, slot.childIndex], { serial: values[i], touched: true });
      }
    });

    if (overcount > 0) {
      notify({ type: "ERROR", message: `${overcount} extra serial(s) in the paste were not used — no more empty slots in this section.` });
    }
  }

  function handleRangeFill() {
    if (!rangeStart.trim()) return;
    const slots = sectionSlots().filter((s) => !s.serial.trim());
    if (slots.length === 0) return;

    const values = fillRangeSerials(rangeStart.trim(), slots.length);
    if (values.length === 1 && slots.length > 1) {
      notify({ type: "ERROR", message: "Couldn't auto-increment this serial format — only the first slot was filled. Enter the rest individually." });
    }

    values.forEach((value, i) => {
      const slot = slots[i];
      if (!slot) return;
      if (slot.childIndex === null) {
        onChange(path, { serial: value, touched: true });
      } else {
        onChange([...path, slot.childIndex], { serial: value, touched: true });
      }
    });
    setRangeStart("");
    setShowRangeFill(false);
  }

  const ownInputEl = (
    <div className="relative">
      <input
        ref={(el: HTMLInputElement | null) => {
          ownInputRef.current = el;
          inputRef?.(el);
        }}
        type="text"
        className={`w-full rounded border p-2 pr-8 text-sm ${ownError ? "border-red-500" : ownFilled ? "border-green-400" : "border-gray-300"}`}
        placeholder={`${node.packagingType} serial — type or scan`}
        value={node.serial}
        onChange={(e) => onChange(path, { serial: e.target.value, touched: true })}
        onKeyDown={handleKeyDown(0)}
        onPaste={!isRoot ? handlePaste : undefined}
        onClick={(e) => e.stopPropagation()}
      />
      {ownFilled && !ownError && (
        <CheckIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
      )}
    </div>
  );

  // Root: plain "{type} serial (N of 1)" label + input, no card/collapse/
  // toolbar — sits above the child sections rather than containing them.
  if (isRoot) {
    return (
      <div className="mb-3 w-full">
        <div className="mb-1 text-xs text-gray-500">
          <span className="capitalize">{node.packagingType} serial</span> ({progress.filled >= 1 ? 1 : 0} of 1)
        </div>
        {ownInputEl}
        {ownError && <p className="mb-1 mt-1 text-xs text-red-600">{ownError}</p>}

        {!isLeaf && (
          <div className="mt-3 flex w-full flex-col gap-2">
            {node.children!.map((child, i) => (
              <InventorySerialSection
                key={i}
                inputRef={(el: HTMLInputElement | null) => {
                  childRefs.current[i] = el;
                }}
                node={child}
                path={[...path, i]}
                onChange={onChange}
                serverErrorBySerial={serverErrorBySerial}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-r border-y border-r border-gray-200 p-3 border-l-4 ${depthAccent} ${
        isComplete ? "bg-green-50" : ""
      } ${node.level > 2 ? "ml-3 w-[calc(100%-0.75rem)]" : "w-full"}`}
    >
      <div
        className={`mb-2 flex items-center justify-between gap-2 ${!isLeaf ? "cursor-pointer select-none" : ""}`}
        onClick={() => !isLeaf && setCollapsed((c) => !c)}
      >
        <span className="flex items-center gap-1.5 text-sm font-medium capitalize">
          {!isLeaf && (
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`h-3 w-3 text-gray-400 transition-transform ${collapsed ? "" : "rotate-90"}`}
            >
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
          )}
          {node.packagingType}
        </span>
        <span
          className={`whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${
            isComplete ? "bg-green-100 text-green-700" : "text-gray-500"
          }`}
        >
          {progress.filled} of {progress.total}
          {isComplete && <CheckIcon className="ml-1 inline h-3 w-3" />}
        </span>
      </div>

      <div className="mb-2">{ownInputEl}</div>
      {ownError && <p className="mb-2 text-xs text-red-600">{ownError}</p>}

      {!isLeaf && (
        <div className={collapsed ? "hidden" : ""}>
          {/* Scan / Range fill / Paste — one toolbar per section, scoped to
              this section's own slot + its direct children's slots. */}
          <div className="mb-2 flex gap-1.5">
            <button
              type="button"
              title="Focus the next empty slot, then scan or type"
              className="flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => focusNextEmpty(-1)}
            >
              <ScanIcon className="h-3.5 w-3.5" /> Scan
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => setShowRangeFill((v) => !v)}
            >
              <RangeFillIcon className="h-3.5 w-3.5" /> Range fill
            </button>
            <button
              type="button"
              title="Focus the next empty slot, then paste a column of serials"
              className="flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => focusNextEmpty(-1)}
            >
              <ClipboardIcon className="h-3.5 w-3.5" /> Paste
            </button>
          </div>

          {showRangeFill && (
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                autoFocus
                className="flex-1 rounded border border-gray-300 p-1 text-xs"
                placeholder="Starting serial"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
              />
              <button type="button" className="rounded bg-gray-100 px-2 py-1 text-xs" onClick={handleRangeFill}>
                Fill
              </button>
              <button
                type="button"
                className="text-xs text-gray-500"
                onClick={() => {
                  setShowRangeFill(false);
                  setRangeStart("");
                }}
              >
                Cancel
              </button>
            </div>
          )}

          <div
            className={childrenAreLeaves ? "grid w-full gap-2" : "flex w-full flex-col gap-2"}
            style={childrenAreLeaves ? { gridTemplateColumns: "repeat(auto-fill, minmax(6.5rem, 1fr))" } : undefined}
          >
            {node.children!.map((child, i) => {
              const childError = serverErrorBySerial[child.serial] || child.error;
              const childFilled = child.serial.trim().length > 0;
              return childrenAreLeaves ? (
                <div key={i} className="relative">
                  <input
                    ref={(el: HTMLInputElement | null) => {
                      childRefs.current[i] = el;
                    }}
                    type="text"
                    className={`w-full rounded border p-2 pr-6 text-sm ${
                      childError ? "border-red-500" : childFilled ? "border-green-400 bg-green-50/50" : "border-gray-300"
                    }`}
                    placeholder={`${child.packagingType} ${i + 1}`}
                    value={child.serial}
                    onChange={(e) => onChange([...path, i], { serial: e.target.value, touched: true })}
                    onKeyDown={handleKeyDown(i + 1)}
                    onPaste={handlePaste}
                  />
                  {childFilled && !childError && (
                    <CheckIcon className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-green-500" />
                  )}
                </div>
              ) : (
                <InventorySerialSection
                  key={i}
                  inputRef={(el: HTMLInputElement | null) => {
                    childRefs.current[i] = el;
                  }}
                  node={child}
                  path={[...path, i]}
                  onChange={onChange}
                  serverErrorBySerial={serverErrorBySerial}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
